import React, { useMemo, useState } from 'react';
import { Plus, Search, X, Wrench, FileText, Upload, Mic, Link as LinkIcon, Filter, ArrowLeft, History, Pencil, Calendar, Clock, User, Trash2 } from 'lucide-react';
import { UserRole, UserType } from '../types';
import { encontrarClientePorTelefone } from '../shared/logica/telefone';
import ModalDetalhesAssistencia from '../paginas/assistencias/ModalDetalhesAssistencia';
import { findNextAvailableSlot } from '../utils/timeUtils';
import { format } from 'date-fns';
import { UNIQUE_COUNTRIES } from '../utils/countries';
import { compressImages } from '../utils/imageUtils';

export default function TechAssistanceView({ company, currentUser, team = [], setTasks, techJobs, budgets, onAdd, onUpdate, onAddBudget, onUpdateBudget, onDeleteBudget, onDelete, clients, tasks = [], onBack, initialMode = 'CREATE' }) {
  const isSimpleUser = currentUser?.userType === UserType.SIMPLE;
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(isSimpleUser ? 'BUDGET' : 'TECH'); // Para simples, começar em BUDGET
  const [showCreateMenu, setShowCreateMenu] = useState(isSimpleUser && initialMode === 'CREATE'); // Abrir automaticamente para simples apenas se for CREATE
  const [showNewTech, setShowNewTech] = useState(false);
  const [showNewBudget, setShowNewBudget] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [selectedAssist, setSelectedAssist] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterUsers, setFilterUsers] = useState([]);
  const [techCountryCode, setTechCountryCode] = useState('351'); // Padrão: Portugal
  const [budgetCountryCode, setBudgetCountryCode] = useState('351'); // Padrão: Portugal
  const [viewMode, setViewMode] = useState('MAIN'); // 'MAIN' or 'HISTORY'
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduledTaskData, setScheduledTaskData] = useState(null); // Store manually scheduled task data
  const [hasScheduledTask, setHasScheduledTask] = useState(false); // Track if task was manually scheduled
  const [sortBy, setSortBy] = useState('newer'); // 'a-z', 'location', 'newer', 'older'

  // Render-safe money formatting: Firestore can sometimes return numbers as strings.
  const formatMoney = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value.toFixed(2);
    if (typeof value === 'string') {
      const normalized = value.replace(',', '.').trim();
      const n = Number(normalized);
      if (Number.isFinite(n)) return n.toFixed(2);
    }
    if (value === null || value === undefined) return '0.00';
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : '0.00';
  };

  const [techForm, setTechForm] = useState({
    client: '',
    phone: '',
    locality: '',
    locationLink: '',
    brand: '',
    reference: '',
    faultDate: new Date().toISOString().slice(0, 10),
    description: '',
    isWarranty: false
  });
  const [photos, setPhotos] = useState([]); // base64 previews

  const [budgetForm, setBudgetForm] = useState({
    client: '',
    location: '',
    phone: '',
    date: new Date().toISOString().slice(0, 10),
    details: '',
    items: []
  });

  const resetBudgetForm = () => {
    setBudgetForm({
      client: '',
      location: '',
      phone: '',
      date: new Date().toISOString().slice(0, 10),
      details: '',
      items: []
    });
    setBudgetCountryCode('351'); // Reset para Portugal
  };

  const resetTechForm = () => {
    setTechForm({
      client: '',
      phone: '',
      locality: '',
      locationLink: '',
      brand: '',
      reference: '',
      faultDate: new Date().toISOString().slice(0, 10),
      description: '',
      isWarranty: false
    });
    setPhotos([]);
    setTechCountryCode('351'); // Reset para Portugal
    setShowScheduleModal(false);
    setScheduledTaskData(null);
    setHasScheduledTask(false);
  };

  // Filter jobs based on view mode (main vs history)
  // History: CONCLUIDA status AND isPaid === true
  // Main: everything else (not concluded OR not paid)
  const jobsForMain = techJobs.filter(tj => {
    if (tj.isArchived) return false;
    const status = tj.status || 'ABERTA';
    const isPaid = tj.isPaid === true;
    // Main view: not (CONCLUIDA AND paid)
    return !(status === 'CONCLUIDA' && isPaid);
  });

  const jobsForHistory = techJobs.filter(tj => {
    if (tj.isArchived) return false;
    const status = tj.status || 'ABERTA';
    const isPaid = tj.isPaid === true;
    // History: CONCLUIDA AND paid
    return status === 'CONCLUIDA' && isPaid;
  });

  const filteredJobs = viewMode === 'HISTORY' ? jobsForHistory : jobsForMain;

  const filteredJobsWithSearch = useMemo(() => {
    // First filter
    let filtered = filteredJobs.filter(tj => {
      const matchesSearch = 
        (tj.client || '').toLowerCase().includes(search.toLowerCase()) || 
        (tj.locality || '').toLowerCase().includes(search.toLowerCase());
      
      const matchesUser = filterUsers.length === 0 || (tj.createdBy && filterUsers.includes(tj.createdBy));
      
      return matchesSearch && matchesUser;
    });

    // Then sort
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'a-z':
          const clientA = (a.client || '').toLowerCase();
          const clientB = (b.client || '').toLowerCase();
          return clientA.localeCompare(clientB);
        
        case 'location':
          const locA = (a.locality || '').toLowerCase();
          const locB = (b.locality || '').toLowerCase();
          return locA.localeCompare(locB);
        
        case 'newer':
          const dateA = new Date(a.date || a.faultDate || 0);
          const dateB = new Date(b.date || b.faultDate || 0);
          // Invalid dates (0) go to the end
          if (dateA.getTime() === 0 && dateB.getTime() !== 0) return 1;
          if (dateB.getTime() === 0 && dateA.getTime() !== 0) return -1;
          return dateB - dateA; // Newest first
        
        case 'older':
          const dateA2 = new Date(a.date || a.faultDate || 0);
          const dateB2 = new Date(b.date || b.faultDate || 0);
          // Invalid dates (0) go to the end
          if (dateA2.getTime() === 0 && dateB2.getTime() !== 0) return 1;
          if (dateB2.getTime() === 0 && dateA2.getTime() !== 0) return -1;
          return dateA2 - dateB2; // Oldest first
        
        default:
          return 0;
      }
    });

    return sorted;
  }, [filteredJobs, search, filterUsers, sortBy]);


  const filteredBudgets = useMemo(() => {
    // First filter
    let filtered = budgets.filter(b => {
      const searchLower = search.toLowerCase();
      return (
        (b.client || '').toLowerCase().includes(searchLower) ||
        (b.location || '').toLowerCase().includes(searchLower) ||
        (b.phone || '').toLowerCase().includes(searchLower) ||
        (b.referenceNumber || '').toLowerCase().includes(searchLower) ||
        (b.id || '').toLowerCase().includes(searchLower)
      );
    });

    // Then sort
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'a-z':
          const clientA = (a.client || '').toLowerCase();
          const clientB = (b.client || '').toLowerCase();
          return clientA.localeCompare(clientB);
        
        case 'location':
          const locA = (a.location || '').toLowerCase();
          const locB = (b.location || '').toLowerCase();
          return locA.localeCompare(locB);
        
        case 'newer':
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          // Invalid dates (0) go to the end
          if (dateA.getTime() === 0 && dateB.getTime() !== 0) return 1;
          if (dateB.getTime() === 0 && dateA.getTime() !== 0) return -1;
          return dateB - dateA; // Newest first
        
        case 'older':
          const dateA2 = new Date(a.date || 0);
          const dateB2 = new Date(b.date || 0);
          // Invalid dates (0) go to the end
          if (dateA2.getTime() === 0 && dateB2.getTime() !== 0) return 1;
          if (dateB2.getTime() === 0 && dateA2.getTime() !== 0) return -1;
          return dateA2 - dateB2; // Oldest first
        
        default:
          return 0;
      }
    });

    return sorted;
  }, [budgets, search, sortBy]);

  // lookup inteligente por telefone (preenche cliente/localidade)
  const matchedClient = useMemo(() => {
    return encontrarClientePorTelefone(clients, techForm.phone, techCountryCode);
  }, [clients, techForm.phone, techCountryCode]);

  const openCreateMenu = () => setShowCreateMenu(true);
  const closeCreateMenu = () => setShowCreateMenu(false);

  const openNewTech = () => {
    resetTechForm();
    setShowCreateMenu(false);
    setShowNewTech(true);
  };
  const closeNewTech = () => {
    setShowNewTech(false);
    resetTechForm();
    // Para utilizadores simples, voltar para o menu ao fechar (deixa o componente re-renderizar)
  };

  const openNewBudget = () => {
    resetBudgetForm();
    setShowCreateMenu(false);
    setShowNewBudget(true);
  };
  const closeNewBudget = () => {
    setShowNewBudget(false);
    resetBudgetForm();
    // Para utilizadores simples, voltar para o menu ao fechar (deixa o componente re-renderizar)
  };

  const openEditBudget = (budget) => {
    // Extrair código do país do telefone se existir
    let phoneNumber = budget.phone || '';
    let countryCode = '351'; // Padrão
    if (phoneNumber.startsWith('+')) {
      // Tentar extrair código do país
      for (const country of UNIQUE_COUNTRIES) {
        if (phoneNumber.startsWith(`+${country.code}`)) {
          countryCode = country.code;
          phoneNumber = phoneNumber.substring(country.code.length + 1);
          break;
        }
      }
    }
    
    setBudgetForm({
      client: budget.client || '',
      location: budget.location || '',
      phone: phoneNumber,
      date: budget.date || new Date().toISOString().slice(0, 10),
      details: budget.details || '',
      items: budget.items?.map((item, idx) => ({
        id: item.id || `item-${Date.now()}-${idx}`,
        category: item.category,
        description: item.description || '',
        grade: item.grade || '',
        value: item.value || ''
      })) || []
    });
    setBudgetCountryCode(countryCode);
    setEditingBudget(budget);
    setSelectedBudget(null); // Fechar modal de detalhes
  };
  const closeEditBudget = () => {
    setEditingBudget(null);
    resetBudgetForm();
  };


  const budgetCategories = [
    { id: 'ELECTRODOMESTICOS', label: 'Electrodomésticos', icon: '🏠' },
    { id: 'PAINEIS_SOLARES', label: 'Painéis Solares', icon: '☀️' },
    { id: 'AR_CONDICIONADO', label: 'Ar Condicionado', icon: '❄️' },
    { id: 'ANTENA', label: 'Antena', icon: '📡' },
    { id: 'OUTROS', label: 'Outros', icon: '📦' }
  ];

  const addBudgetItem = (categoryId) => {
    setBudgetForm(prev => ({
      ...prev,
      items: [...prev.items, {
        id: `item-${Date.now()}-${Math.random()}`,
        category: categoryId,
        description: '',
        grade: '',
        value: ''
      }]
    }));
  };

  const removeBudgetItem = (itemId) => {
    setBudgetForm(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const updateBudgetItem = (itemId, field, value) => {
    setBudgetForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateBudgetTotal = () => {
    return budgetForm.items.reduce((sum, item) => {
      const value = parseFloat(item.value) || 0;
      return sum + value;
    }, 0);
  };

  const submitNewBudget = (e) => {
    e.preventDefault();

    const total = calculateBudgetTotal();
    
    const budgetPayload = {
      client: budgetForm.client,
      location: budgetForm.location,
      phone: budgetForm.phone.startsWith('+') || budgetForm.phone.startsWith('00') 
        ? budgetForm.phone 
        : `+${budgetCountryCode}${budgetForm.phone}`,
      date: budgetForm.date,
      details: budgetForm.details,
      status: 'PENDENTE', // Always start as PENDENTE
      total: total,
      items: budgetForm.items.map(item => ({
        category: item.category,
        description: item.description,
        grade: item.grade,
        value: parseFloat(item.value) || 0
      }))
    };

    onAddBudget(budgetPayload);
    closeNewBudget();
    // Para utilizadores simples, não voltar ao dashboard, deixa voltar ao menu
  };

  const submitEditBudget = (e) => {
    e.preventDefault();

    if (!editingBudget) return;

    const total = calculateBudgetTotal();
    
    const updatedBudget = {
      ...editingBudget,
      client: budgetForm.client,
      location: budgetForm.location,
      phone: budgetForm.phone.startsWith('+') || budgetForm.phone.startsWith('00') 
        ? budgetForm.phone 
        : `+${budgetCountryCode}${budgetForm.phone}`,
      date: budgetForm.date,
      details: budgetForm.details,
      total: total,
      items: budgetForm.items.map(item => ({
        category: item.category,
        description: item.description,
        grade: item.grade,
        value: parseFloat(item.value) || 0
      }))
    };

    if (onUpdateBudget) {
      onUpdateBudget(updatedBudget);
    }
    closeEditBudget();
  };

  const handlePhotoUpload = async (files) => {
    try {
      // Comprimir imagens antes de converter para base64
      // maxWidth: 1920, maxHeight: 1920, quality: 0.8, maxSizeKB: 400
      const compressedPhotos = await compressImages(files, 1920, 1920, 0.8, 400);
      setPhotos(prev => [...prev, ...compressedPhotos].slice(0, 8));
    } catch (error) {
      console.error('Erro ao comprimir fotos:', error);
      // Fallback: tentar sem compressão
      const arr = Array.from(files || []);
      const readers = arr.map(file => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.readAsDataURL(file);
      }));
      const base64s = await Promise.all(readers);
      setPhotos(prev => [...prev, ...base64s].slice(0, 8));
    }
  };

  const submitNewTech = (e) => {
    e.preventDefault();

    const jobId = `tech-${Date.now()}`;
    // Garantir que photos seja sempre um array válido
    const photosToSubmit = Array.isArray(photos) ? photos : [];
    
    const payload = {
      id: jobId,
      date: new Date().toISOString().slice(0, 10),
      client: matchedClient?.name || techForm.client,
      phone: techForm.phone.startsWith('+') || techForm.phone.startsWith('00') 
        ? techForm.phone 
        : `+${techCountryCode}${techForm.phone}`,
      locality: matchedClient?.locality || techForm.locality,
      locationLink: techForm.locationLink,
      brand: techForm.brand,
      reference: techForm.reference,
      faultDate: techForm.faultDate,
      description: techForm.description,
      isWarranty: techForm.isWarranty,
      photos: photosToSubmit // Sempre usar array válido
    };

    onAdd(payload);

    // Garantia => criar tarefa automática para EMPLOYEE
    if (techForm.isWarranty && typeof setTasks === 'function' && !hasScheduledTask) {
      // Encontrar primeiro EMPLOYEE disponível
      const employee = team.find(u => u.role === UserRole.EMPLOYEE) || team.find(u => u.role === UserRole.OWNER) || currentUser;
      if (employee) {
        // Obter hora atual
        const now = new Date();
        const currentTime = format(now, 'HH:mm');
        const taskDate = payload.faultDate || format(now, 'yyyy-MM-dd');
        
        // Encontrar próximo slot disponível com verificação de conflitos
        const tasksForEmployeeOnDate = tasks.filter(t => 
          t.assignedTo === employee.id && 
          t.date === taskDate &&
          t.startTime && 
          t.endTime
        );
        
        const { startTime, endTime } = findNextAvailableSlot(
          currentTime,
          tasksForEmployeeOnDate,
          employee.id,
          taskDate
        );
        
        setTasks(prev => [...prev, {
          id: `task-warranty-${Date.now()}`,
          companyId: company.id,
          name: 'Tratar da Garantia',
          date: taskDate,
          startTime: startTime,
          endTime: endTime,
          assignedTo: employee.id,
          isCompleted: false,
          status: 'A Fazer',
          relatedType: 'TECH',
          relatedId: jobId,
          notes: `Cliente: ${payload.client || ''}\n${payload.locality ? `Localidade: ${payload.locality}\n` : ''}${payload.phone ? `Tel: ${payload.phone}\n` : ''}${payload.locationLink ? `Link: ${payload.locationLink}` : ''}`.trim()
        }]);
      }
    }

    // Se foi agendado manualmente (Marcar na Agenda), criar tarefa
    if (hasScheduledTask && scheduledTaskData && typeof setTasks === 'function') {
      setTasks(prev => [...prev, {
        id: `task-scheduled-${Date.now()}`,
        companyId: company.id,
        name: `Garantia: ${payload.client} (${payload.brand || '—'} ${payload.reference || ''})`,
        date: scheduledTaskData.date,
        startTime: scheduledTaskData.startTime,
        endTime: scheduledTaskData.endTime,
        assignedTo: scheduledTaskData.assignedTo,
        isCompleted: false,
        status: 'A Fazer',
        relatedType: 'TECH',
        relatedId: jobId,
        notes: `${payload.locality || ''}\nTel: ${payload.phone || ''}\nLink: ${payload.locationLink || ''}`.trim()
      }]);
    }

    closeNewTech();
    // Para utilizadores simples, não voltar ao dashboard, deixa voltar ao menu
  };

  // Nova função para criar tarefa ao clicar em produto sob garantia existente (reservada para uso futuro)
  // eslint-disable-next-line no-unused-vars
  const _handleCreateWarrantyTask = (job) => {
    if (!job.isWarranty) return;
    
    const owner = team.find(u => u.role === UserRole.OWNER) || team.find(u => u.role === UserRole.ADMIN) || currentUser;
    if (!owner || typeof setTasks !== 'function') return;
    
    // Verificar se já existe tarefa para este tech job
    const existingTaskForJob = tasks.find(t => 
      t.relatedType === 'TECH' && 
      t.relatedId === job.id &&
      !t.isCompleted
    );
    
    if (existingTaskForJob) {
      alert('Já existe uma tarefa ativa criada para esta assistência técnica sob garantia.');
      return;
    }
    
    // Obter hora atual
    const now = new Date();
    const currentTime = format(now, 'HH:mm');
    const taskDate = job.faultDate || job.date || format(now, 'yyyy-MM-dd');
    
    // Encontrar próximo slot disponível com verificação de conflitos
    const tasksForOwnerOnDate = tasks.filter(t => 
      t.assignedTo === owner.id && 
      t.date === taskDate &&
      t.startTime && 
      t.endTime
    );
    
    const { startTime, endTime } = findNextAvailableSlot(
      currentTime,
      tasksForOwnerOnDate,
      owner.id,
      taskDate
    );
    
    setTasks(prev => [...prev, {
      id: `task-warranty-${Date.now()}-${job.id}`,
      companyId: company.id,
      name: `Garantia: ${job.client} (${job.brand || '—'} ${job.reference || ''})`,
      date: taskDate,
      startTime: startTime,
      endTime: endTime,
      assignedTo: owner.id,
      isCompleted: false,
      status: 'A Fazer',
      relatedType: 'TECH',
      relatedId: job.id,
      notes: `${job.locality || ''}\nTel: ${job.phone || ''}\nLink: ${job.locationLink || ''}`.trim()
    }]);
  };

  // Para utilizadores simples, mostrar apenas o menu de criar se não estiver criando nada E se o modo inicial for CREATE
  if (isSimpleUser && initialMode === 'CREATE' && !showNewTech && !showNewBudget && !selectedAssist && !selectedBudget) {
    return (
      <>
        {/* Botão de voltar fixo */}
        {onBack && (
          <div className="fixed top-4 left-4 z-[121]">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white rounded-xl transition-colors bg-white shadow-sm border border-slate-200"
            >
              <ArrowLeft size={24} className="text-slate-600" />
            </button>
          </div>
        )}
        
        {/* Modal: menu criar - sempre visível para simples */}
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[120] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] p-10 w-full max-w-sm shadow-2xl space-y-5 text-center">
            <h2 className="text-xl font-black text-slate-900">O que pretende criar?</h2>
            <button
              onClick={openNewTech}
              className="w-full py-4 bg-green-800 text-white font-black rounded-2xl shadow-xl flex items-center justify-center gap-2 hover:bg-green-900 transition-all active:scale-[0.98]"
            >
              <Wrench size={20} /> Criar Assistência
            </button>
            <button
              onClick={openNewBudget}
              className="w-full py-4 border-2 border-green-800 text-green-900 font-black rounded-2xl flex items-center justify-center gap-2 hover:bg-green-50 transition-all active:scale-[0.98]"
            >
              <FileText size={20} /> Criar Orçamento
            </button>
            <button 
              onClick={onBack || (() => setShowCreateMenu(false))} 
              className="w-full py-2 text-slate-400 font-black hover:text-slate-600 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={`space-y-6 ${isSimpleUser ? 'p-4 sm:p-6' : ''}`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {isSimpleUser && onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-100 rounded-xl transition-colors bg-white shadow-sm border border-slate-200 flex-shrink-0"
            >
              <ArrowLeft size={20} className="sm:w-6 sm:h-6 text-slate-600" />
            </button>
          )}
          {(!isSimpleUser || (isSimpleUser && initialMode === 'VIEW')) && (
            <div className={`flex bg-white p-1 rounded-xl sm:rounded-2xl border border-green-100 shadow-sm ${isSimpleUser ? 'flex-1 min-w-0' : ''}`}>
              <button 
                onClick={() => {
                  setTab('TECH');
                  setViewMode('MAIN');
                }}
                className={`${isSimpleUser ? 'flex-1 px-3 sm:px-4 py-2 rounded-lg font-bold text-xs sm:text-sm' : 'px-6 py-2 rounded-xl font-bold text-sm'} transition-all ${tab === 'TECH' ? 'bg-[#1b5e20] text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
              >
                {isSimpleUser ? (
                  <>
                    <span className="hidden sm:inline">Assistências </span>
                    <span className="sm:hidden">Assist.</span>
                  </>
                ) : (
                  'Assistências '
                )}
                ({filteredJobsWithSearch.length})
              </button>
              <button 
                onClick={() => setTab('BUDGET')}
                className={`${isSimpleUser ? 'flex-1 px-3 sm:px-4 py-2 rounded-lg font-bold text-xs sm:text-sm' : 'px-6 py-2 rounded-xl font-bold text-sm'} transition-all ${tab === 'BUDGET' ? 'bg-[#1b5e20] text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
              >
                {isSimpleUser ? (
                  <>
                    <span className="hidden sm:inline">Orçamentos </span>
                    <span className="sm:hidden">Orçam.</span>
                  </>
                ) : (
                  'Orçamentos '
                )}
                ({filteredBudgets.length})
              </button>
            </div>
          )}
        </div>
        {!isSimpleUser && (
          <button 
            onClick={openCreateMenu}
            className="bg-green-800 text-white px-6 py-4 rounded-xl font-black flex items-center gap-2 hover:bg-green-900 shadow-md transition-all active:scale-95"
          >
            <Plus size={24} /> Criar Novo
          </button>
        )}
      </div>

          {(!isSimpleUser || (isSimpleUser && initialMode === 'VIEW')) && (
            <>
          <div className="space-y-4">
            {/* Search Bar */}
            <div className={`bg-white ${isSimpleUser ? 'p-3 sm:p-4 rounded-xl flex flex-col sm:flex-row gap-3' : 'p-6 rounded-2xl flex gap-4'} shadow-lg border border-green-100`}>
              <div className="relative flex-1 min-w-0">
                <Search className={`absolute ${isSimpleUser ? 'left-3 sm:left-4' : 'left-4'} top-1/2 -translate-y-1/2 text-green-400 ${isSimpleUser ? 'w-[18px] h-[18px] sm:w-6 sm:h-6' : ''}`} size={isSimpleUser ? 18 : 24} />
                <input 
                  type="text" 
                  placeholder={isSimpleUser ? "Pesquisar..." : "Pesquisar por Cliente, Nome, Número, Marca ou Referência..."} 
                  className={`w-full ${isSimpleUser ? 'pl-10 sm:pl-14 pr-3 sm:pr-4 py-3 sm:py-4 rounded-xl sm:rounded-2xl text-sm sm:text-xl' : 'pl-14 pr-4 py-4 rounded-2xl text-xl'} bg-green-50 outline-none focus:ring-2 focus:ring-green-600 transition-all`}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              {!isSimpleUser && (
                <div className="relative">
                  <button 
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-6 py-4 rounded-2xl font-black border-2 transition-all ${
                      showFilters || filterUsers.length > 0
                        ? 'bg-green-800 border-green-800 text-white' 
                        : 'bg-green-50 border-green-100 text-green-700'
                    }`}
                  >
                    <Filter size={20} /> Filtros {filterUsers.length > 0 && `(${filterUsers.length})`}
                  </button>

                  {showFilters && (
                    <div className="absolute right-0 mt-3 w-80 bg-white rounded-3xl shadow-2xl border border-green-100 p-6 z-40">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-black text-slate-900 uppercase text-xs tracking-widest">Filtros Avançados</h3>
                        <button 
                          onClick={() => {
                            setFilterUsers([]);
                            setShowFilters(false);
                          }} 
                          className="text-[10px] font-black text-green-700 uppercase"
                        >
                          Limpar
                        </button>
                      </div>
                      
                      {team.length > 0 && (
                        <div className="mb-4">
                          <p className="text-[10px] font-black text-slate-400 uppercase mb-2 px-1">Utilizador</p>
                          <div className="flex flex-wrap gap-2">
                            {team.map(user => (
                              <button 
                                key={user.id} 
                                onClick={() => setFilterUsers(prev => prev.includes(user.id) ? prev.filter(x => x !== user.id) : [...prev, user.id])}
                                className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition-all border ${
                                  filterUsers.includes(user.id) 
                                    ? 'bg-green-100 text-green-800 border-green-200' 
                                    : 'bg-slate-50 text-slate-400 border-slate-100'
                                }`}
                              >
                                {user.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Sort buttons inside filters for mobile only */}
                      {(tab === 'TECH' || tab === 'BUDGET') && viewMode === 'MAIN' && (
                        <div className="mt-4 pt-4 border-t border-slate-200 sm:hidden">
                          <p className="text-[10px] font-black text-slate-400 uppercase mb-2 px-1">Ordenar</p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => setSortBy('a-z')}
                              className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition-all border ${
                                sortBy === 'a-z'
                                  ? 'bg-green-100 text-green-800 border-green-200'
                                  : 'bg-slate-50 text-slate-400 border-slate-100'
                              }`}
                            >
                              A a Z
                            </button>
                            <button
                              onClick={() => setSortBy('location')}
                              className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition-all border ${
                                sortBy === 'location'
                                  ? 'bg-green-100 text-green-800 border-green-200'
                                  : 'bg-slate-50 text-slate-400 border-slate-100'
                              }`}
                            >
                              Localização
                            </button>
                            <button
                              onClick={() => setSortBy('newer')}
                              className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition-all border ${
                                sortBy === 'newer'
                                  ? 'bg-green-100 text-green-800 border-green-200'
                                  : 'bg-slate-50 text-slate-400 border-slate-100'
                              }`}
                            >
                              Recente
                            </button>
                            <button
                              onClick={() => setSortBy('older')}
                              className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition-all border ${
                                sortBy === 'older'
                                  ? 'bg-green-100 text-green-800 border-green-200'
                                  : 'bg-slate-50 text-slate-400 border-slate-100'
                              }`}
                            >
                              Antigo
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sort/Filter Buttons - Show for TECH and BUDGET tabs */}
            {(tab === 'TECH' || tab === 'BUDGET') && (
              <div className={`bg-white ${isSimpleUser ? 'p-3 sm:p-4 rounded-xl' : 'p-4 rounded-2xl'} shadow-lg border border-green-100`}>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {viewMode === 'MAIN' && (
                    <>
                      {/* Sort buttons - hidden on mobile, visible on tablet/desktop */}
                      <div className="hidden sm:flex flex-wrap gap-2 sm:gap-3">
                        <button
                          onClick={() => setSortBy('a-z')}
                          className={`px-4 py-2 rounded-xl font-black text-xs sm:text-sm transition-all ${
                            sortBy === 'a-z'
                              ? 'bg-green-800 text-white shadow-md'
                              : 'bg-green-50 text-green-700 hover:bg-green-100 border-2 border-green-200'
                          }`}
                        >
                          A a Z
                        </button>
                        <button
                          onClick={() => setSortBy('location')}
                          className={`px-4 py-2 rounded-xl font-black text-xs sm:text-sm transition-all ${
                            sortBy === 'location'
                              ? 'bg-green-800 text-white shadow-md'
                              : 'bg-green-50 text-green-700 hover:bg-green-100 border-2 border-green-200'
                          }`}
                        >
                          Localização
                        </button>
                        <button
                          onClick={() => setSortBy('newer')}
                          className={`px-4 py-2 rounded-xl font-black text-xs sm:text-sm transition-all ${
                            sortBy === 'newer'
                              ? 'bg-green-800 text-white shadow-md'
                              : 'bg-green-50 text-green-700 hover:bg-green-100 border-2 border-green-200'
                          }`}
                        >
                          Recente
                        </button>
                        <button
                          onClick={() => setSortBy('older')}
                          className={`px-4 py-2 rounded-xl font-black text-xs sm:text-sm transition-all ${
                            sortBy === 'older'
                              ? 'bg-green-800 text-white shadow-md'
                              : 'bg-green-50 text-green-700 hover:bg-green-100 border-2 border-green-200'
                          }`}
                        >
                          Antigo
                        </button>
                      </div>
                      {!isSimpleUser && tab === 'TECH' && (
                        <button
                          onClick={() => setViewMode('HISTORY')}
                          className="ml-auto px-4 py-2 rounded-xl font-black text-xs sm:text-sm transition-all flex items-center gap-2 bg-green-800 text-white shadow-md hover:bg-green-900"
                        >
                          <History size={16} /> Histórico
                        </button>
                      )}
                    </>
                  )}
                  {viewMode === 'HISTORY' && !isSimpleUser && tab === 'TECH' && (
                    <button
                      onClick={() => setViewMode('MAIN')}
                      className="ml-auto px-4 py-2 rounded-xl font-black text-xs sm:text-sm transition-all flex items-center gap-2 bg-green-800 text-white shadow-md hover:bg-green-900"
                    >
                      <ArrowLeft size={16} /> Voltar
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {!isSimpleUser && viewMode === 'HISTORY' ? (
            // History View - Table Format
            <div className="bg-white rounded-2xl shadow-lg border border-green-100 overflow-hidden">
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-green-50/30">
                <h2 className="text-2xl font-black text-slate-900">Histórico</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-[#1b5e20] text-white/70 text-[10px] font-black uppercase tracking-widest">
                    <tr>
                      <th className="p-4">Data</th>
                      <th className="p-4">Cliente</th>
                      <th className="p-4">Localidade</th>
                      <th className="p-4">Telefone</th>
                      <th className="p-4">Marca</th>
                      <th className="p-4">Referência</th>
                      <th className="p-4 text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredJobsWithSearch.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="p-12 text-center text-slate-400 font-bold">
                          Nenhuma assistência no histórico
                        </td>
                      </tr>
                    ) : (
                      filteredJobsWithSearch.map(job => (
                        <tr key={job.id} className="group hover:bg-green-50/40 transition-all">
                          <td className="p-4">
                            <p className="text-sm font-black text-slate-900">{job.date || '—'}</p>
                          </td>
                          <td className="p-4">
                            <p className="font-black text-slate-900">{job.client || '—'}</p>
                          </td>
                          <td className="p-4">
                            <p className="text-sm text-slate-600 font-bold">{job.locality || '—'}</p>
                          </td>
                          <td className="p-4">
                            <p className="text-sm text-slate-600 font-bold">{job.phone || '—'}</p>
                          </td>
                          <td className="p-4">
                            <p className="text-sm text-slate-600 font-bold">{job.brand || '—'}</p>
                          </td>
                          <td className="p-4">
                            <p className="text-sm text-slate-600 font-bold">{job.reference || '—'}</p>
                          </td>
                          <td className="p-4 text-center">
                            <button
                              onClick={() => setSelectedAssist(job)}
                              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-black text-xs transition-colors"
                            >
                              Ver Detalhes
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <>
              <div className={`grid ${isSimpleUser ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
        {tab === 'TECH' ? filteredJobsWithSearch.map(job => (
          <div 
            key={job.id} 
            className={`bg-white ${isSimpleUser ? 'rounded-xl sm:rounded-2xl p-3 sm:p-4' : 'rounded-3xl p-6'} shadow-xl border border-green-100 hover:border-green-400 transition-all group flex flex-col`}
          >
            <div 
              onClick={() => setSelectedAssist(job)}
              className="flex-grow cursor-pointer"
            >
              <div className={`flex justify-between items-start ${isSimpleUser ? 'mb-3 sm:mb-4' : 'mb-4'}`}>
                <span className={`${isSimpleUser ? 'px-2 sm:px-3 py-1 rounded-full text-[9px] sm:text-[10px]' : 'px-3 py-1 rounded-full text-[10px]'} font-black uppercase ${
                  (job.status || 'ABERTA') === 'ABERTA' ? 'bg-red-100 text-red-700' :
                  (job.status || 'ABERTA') === 'EM_TRATAMENTO' ? 'bg-orange-100 text-orange-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {(job.status || 'ABERTA').replace('_', ' ')}
                </span>
                <p className={`${isSimpleUser ? 'text-xs sm:text-sm' : 'text-sm'} font-bold text-green-600`}>{job.date}</p>
              </div>
              <h2 className={`${isSimpleUser ? 'text-lg sm:text-2xl' : 'text-2xl'} font-black text-green-900 mb-1 truncate`}>{job.client}</h2>
              <div className={`${isSimpleUser ? 'space-y-1.5 sm:space-y-2 mb-4 sm:mb-6 text-xs sm:text-sm' : 'space-y-2 mb-6 text-sm'} flex-grow`}>
                <div className={`flex items-center gap-2 text-slate-500 font-bold ${isSimpleUser ? 'truncate' : ''}`}>{job.locality}</div>
                <div className={`flex items-center gap-2 text-slate-500 font-bold ${isSimpleUser ? 'truncate' : ''}`}>{job.phone}</div>
                {job.isWarranty && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-1 bg-green-100 text-green-700 rounded-lg ${isSimpleUser ? 'text-[9px] sm:text-[10px]' : 'text-[10px]'} font-black uppercase`}>
                      ✓ Sob Garantia
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )) : filteredBudgets.map(budget => (
          <div 
            key={budget.id} 
            onClick={() => setSelectedBudget(budget)}
            className={`bg-white ${isSimpleUser ? 'rounded-xl sm:rounded-2xl p-3 sm:p-4' : 'rounded-3xl p-6'} shadow-xl border border-green-100 hover:border-green-400 transition-all cursor-pointer group flex flex-col`}
          >
            <div className={`flex justify-between items-start ${isSimpleUser ? 'mb-3 sm:mb-4' : 'mb-4'}`}>
              <span className={`${isSimpleUser ? 'px-2 sm:px-3 py-1 rounded-full text-[9px] sm:text-[10px]' : 'px-3 py-1 rounded-full text-[10px]'} font-black uppercase ${
                budget.status === 'PENDENTE' ? 'bg-amber-100 text-amber-700' :
                budget.status === 'APROVADO' ? 'bg-green-100 text-green-700' :
                'bg-red-100 text-red-700'
              }`}>
                {budget.status}
              </span>
              <p className={`${isSimpleUser ? 'text-xs sm:text-sm' : 'text-sm'} font-bold text-green-600`}>{budget.date}</p>
            </div>
            <h2 className={`${isSimpleUser ? 'text-lg sm:text-2xl' : 'text-2xl'} font-black text-green-900 mb-1 truncate`}>{budget.client || budget.location || '—'}</h2>
            <div className={`${isSimpleUser ? 'space-y-1.5 sm:space-y-2 mb-4 sm:mb-6 text-xs sm:text-sm' : 'space-y-2 mb-6 text-sm'} flex-grow`}>
              {budget.location && (
                <div className={`flex items-center gap-2 text-slate-500 font-bold ${isSimpleUser ? 'truncate' : ''}`}>{budget.location}</div>
              )}
              {budget.phone && (
                <div className={`flex items-center gap-2 text-slate-500 font-bold ${isSimpleUser ? 'truncate' : ''}`}>{budget.phone}</div>
              )}
              <div className={`flex items-center justify-between text-slate-900 font-black ${isSimpleUser ? 'text-base sm:text-lg' : 'text-lg'}`}>
                <span>{isSimpleUser ? 'Total:' : 'Total Orçamentado:'}</span>
                <span>{formatMoney(budget.total)}€</span>
              </div>
              {budget.items && budget.items.length > 0 && (
                <div className="text-xs text-slate-500 font-bold mt-2">
                  {budget.items.length} {budget.items.length === 1 ? 'item' : 'itens'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
        </>
      )}
        </>
      )}

      {/* Modal: menu criar */}
      {showCreateMenu && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[120] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] p-10 w-full max-w-sm shadow-2xl space-y-5 text-center">
            <h2 className="text-xl font-black text-slate-900">O que pretende criar?</h2>
            <button
              onClick={openNewTech}
              className="w-full py-4 bg-green-800 text-white font-black rounded-2xl shadow-xl flex items-center justify-center gap-2 hover:bg-green-900 transition-all"
            >
              <Wrench size={20} /> Criar Assistência
            </button>
            <button
              onClick={openNewBudget}
              className="w-full py-4 border-2 border-green-800 text-green-900 font-black rounded-2xl flex items-center justify-center gap-2 hover:bg-green-50 transition-all"
            >
              <FileText size={20} /> Criar Orçamento
            </button>
            <button onClick={closeCreateMenu} className="w-full py-2 text-slate-400 font-black">Cancelar</button>
          </div>
        </div>
      )}

      {/* Modal: Nova Assistência */}
      {showNewTech && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[130] flex items-center justify-center p-4">
          <form onSubmit={submitNewTech} className="bg-white rounded-[2.5rem] p-10 w-full max-w-4xl shadow-2xl space-y-6 max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-start">
              <h2 className="text-3xl font-black text-green-800 tracking-tight">Nova Assistência</h2>
              <button 
                type="button" 
                onClick={() => {
                  closeNewTech();
                  if (isSimpleUser && onBack) {
                    // Voltar ao dashboard ao fechar X para simples
                    setTimeout(() => onBack(), 100);
                  }
                }} 
                className="p-2 bg-slate-50 rounded-full hover:bg-slate-100 transition-colors"
              >
                <X />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Cliente *</label>
                <input
                  required
                  value={matchedClient?.name || techForm.client}
                  onChange={(e) => setTechForm(prev => ({ ...prev, client: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                  placeholder="Nome do Cliente"
                />
                <Mic size={16} className="absolute right-4 top-12 text-green-700/40" />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Telefone *</label>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-4">
                    <select
                      value={techCountryCode}
                      onChange={(e) => setTechCountryCode(e.target.value)}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black text-xs outline-none border-2 border-transparent focus:border-green-600"
                    >
                      {UNIQUE_COUNTRIES.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.flag} +{country.code}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-8 relative">
                    <input
                      required
                      value={techForm.phone}
                      onChange={(e) => setTechForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                      placeholder={techCountryCode === '351' ? 'Ex: 912 345 678' : 'Ex: 123456789'}
                    />
                    <Mic size={16} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-green-700/40" />
                  </div>
                </div>
              </div>

              <div className="relative">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Localidade *</label>
                <input
                  required
                  value={matchedClient?.locality || techForm.locality}
                  onChange={(e) => setTechForm(prev => ({ ...prev, locality: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                  placeholder="Morada / Localidade"
                />
                <Mic size={16} className="absolute right-4 top-12 text-green-700/40" />
              </div>

              <div className="relative">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Link Localização (Maps/Waze)</label>
                <input
                  value={techForm.locationLink}
                  onChange={(e) => setTechForm(prev => ({ ...prev, locationLink: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                  placeholder="URL para localização..."
                />
                <LinkIcon size={16} className="absolute right-4 top-12 text-green-700/40" />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Marca</label>
                <input
                  value={techForm.brand}
                  onChange={(e) => setTechForm(prev => ({ ...prev, brand: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                  placeholder="Ex: Sony, Bosch..."
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2"># Referência</label>
                <input
                  value={techForm.reference}
                  onChange={(e) => setTechForm(prev => ({ ...prev, reference: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                  placeholder="Modelo/Nº Série"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Data Avaria</label>
                <input
                  type="date"
                  value={techForm.faultDate}
                  onChange={(e) => setTechForm(prev => ({ ...prev, faultDate: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                />
              </div>
            </div>

            <div className="relative">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Descrição do Problema *</label>
              <textarea
                required
                rows={4}
                value={techForm.description}
                onChange={(e) => setTechForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                placeholder="Detalhes técnicos da avaria..."
              />
              <Mic size={16} className="absolute right-4 top-12 text-green-700/40" />
            </div>

            {/* Two buttons side by side */}
            <div className="grid grid-cols-2 gap-4">
              {/* Produto sob Garantia button */}
              <button
                type="button"
                onClick={() => {
                  const newWarrantyValue = !techForm.isWarranty;
                  setTechForm(prev => ({ ...prev, isWarranty: newWarrantyValue }));
                  // If checking warranty, clear scheduled task
                  if (newWarrantyValue) {
                    setScheduledTaskData(null);
                    setHasScheduledTask(false);
                  }
                }}
                className={`flex items-center gap-4 p-5 rounded-2xl border-2 transition-colors ${
                  techForm.isWarranty ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-100'
                }`}
              >
                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${techForm.isWarranty ? 'bg-green-800 border-green-800' : 'bg-white border-slate-200'}`}>
                  {techForm.isWarranty && <span className="text-white font-black">✓</span>}
                </div>
                <span className="font-black text-slate-800">Produto sob Garantia?</span>
              </button>

              {/* Marcar na Agenda button - only shows when warranty is NOT checked */}
              {!techForm.isWarranty && (
                <button
                  type="button"
                  onClick={() => {
                    // Initialize schedule data with defaults - prefer EMPLOYEE first
                    const assignableTeam = team
                      .filter(u => u?.role !== UserRole.ADMIN)
                      .sort((a, b) => {
                        // Sort: EMPLOYEE first, then others
                        if (a.role === UserRole.EMPLOYEE && b.role !== UserRole.EMPLOYEE) return -1;
                        if (a.role !== UserRole.EMPLOYEE && b.role === UserRole.EMPLOYEE) return 1;
                        return 0;
                      });
                    const defaultUser = assignableTeam.length > 0 ? assignableTeam[0] : currentUser;
                    setScheduledTaskData({
                      date: techForm.faultDate || new Date().toISOString().slice(0, 10),
                      startTime: '09:00',
                      endTime: '10:00',
                      assignedTo: defaultUser?.id || ''
                    });
                    setShowScheduleModal(true);
                  }}
                  className={`flex items-center gap-4 p-5 rounded-2xl border-2 transition-colors ${
                    hasScheduledTask ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-100'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${hasScheduledTask ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-200'}`}>
                    {hasScheduledTask && <span className="text-white font-black">✓</span>}
                  </div>
                  <span className="font-black text-slate-800">Marcar na Agenda</span>
                </button>
              )}
            </div>

            <div className="bg-green-50/60 border-2 border-dashed border-green-200 rounded-3xl p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] font-black text-green-800 uppercase tracking-widest">Fotos do estado de entrada</p>
                <label className="bg-green-800 text-white px-5 py-3 rounded-xl font-black flex items-center gap-2 cursor-pointer hover:bg-green-900 transition-colors">
                  <Upload size={18} /> Upload
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handlePhotoUpload(e.target.files)} />
                </label>
              </div>

              {photos.length === 0 ? (
                <div className="text-center py-10 text-green-300 font-black">
                  SEM EVIDÊNCIAS CAPTURADAS
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {photos.map((p, idx) => (
                    <div key={idx} className="relative group">
                      <img src={p} alt={`foto-${idx}`} className="w-full h-28 object-cover rounded-2xl border border-green-100" />
                      <button
                        type="button"
                        onClick={() => {
                          setPhotos(prev => prev.filter((_, i) => i !== idx));
                        }}
                        className="absolute top-2 right-2 bg-white/90 border border-slate-200 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remover"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-2">
              <button 
                type="button" 
                onClick={() => {
                  closeNewTech();
                  if (isSimpleUser && onBack) {
                    // Voltar ao dashboard após cancelar para simples
                    setTimeout(() => onBack(), 100);
                  }
                }} 
                className="flex-1 py-4 rounded-2xl font-black bg-slate-100 text-slate-400 hover:bg-slate-200 transition-colors"
              >
                {isSimpleUser ? 'Voltar' : 'Cancelar'}
              </button>
              <button type="submit" className="flex-[2] py-4 rounded-2xl font-black bg-green-800 text-white hover:bg-green-900 transition-colors shadow-xl">
                Registar Assistência
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal: Schedule Task (Mark on Agenda) */}
      {showScheduleModal && scheduledTaskData && (
        <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl z-[130] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-10 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl sm:text-2xl font-black text-slate-900">Marcar na Agenda</h2>
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  setScheduledTaskData(null);
                }}
                className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Date Selection */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2 flex items-center gap-2">
                  <Calendar size={14} />
                  Data
                </label>
                <input
                  type="date"
                  value={scheduledTaskData.date}
                  onChange={(e) => setScheduledTaskData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-blue-600"
                />
              </div>

              {/* Time Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2 flex items-center gap-2">
                    <Clock size={14} />
                    Hora Início
                  </label>
                  <input
                    type="time"
                    value={scheduledTaskData.startTime}
                    onChange={(e) => setScheduledTaskData(prev => ({ ...prev, startTime: e.target.value }))}
                    className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-blue-600"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2 flex items-center gap-2">
                    <Clock size={14} />
                    Hora Fim
                  </label>
                  <input
                    type="time"
                    value={scheduledTaskData.endTime}
                    onChange={(e) => setScheduledTaskData(prev => ({ ...prev, endTime: e.target.value }))}
                    className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-blue-600"
                  />
                </div>
              </div>

              {/* User Selection */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2 flex items-center gap-2">
                  <User size={14} />
                  Utilizador
                </label>
                <select
                  value={scheduledTaskData.assignedTo}
                  onChange={(e) => setScheduledTaskData(prev => ({ ...prev, assignedTo: e.target.value }))}
                  className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-blue-600"
                >
                  {team
                    .filter(u => u?.role !== UserRole.ADMIN)
                    .sort((a, b) => {
                      // Sort: EMPLOYEE first, then others
                      if (a.role === UserRole.EMPLOYEE && b.role !== UserRole.EMPLOYEE) return -1;
                      if (a.role !== UserRole.EMPLOYEE && b.role === UserRole.EMPLOYEE) return 1;
                      return 0;
                    })
                    .map(user => (
                      <option key={user.id} value={user.id}>
                        {user.role === UserRole.EMPLOYEE ? 'Empregado' : (user.name || 'Sem nome')}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  setScheduledTaskData(null);
                }}
                className="flex-1 py-3 bg-slate-100 text-slate-600 font-black rounded-xl hover:bg-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (scheduledTaskData.assignedTo && scheduledTaskData.date && scheduledTaskData.startTime && scheduledTaskData.endTime) {
                    setHasScheduledTask(true);
                    setShowScheduleModal(false);
                    // scheduledTaskData is already set, will be used in submitNewTech
                  } else {
                    alert('Por favor, preencha todos os campos.');
                  }
                }}
                className="flex-1 py-3 bg-blue-600 text-white font-black rounded-xl hover:bg-blue-700 transition-colors"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Novo Orçamento */}
      {showNewBudget && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[120] flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={submitNewBudget} className="bg-white rounded-[2.5rem] p-10 w-full max-w-4xl shadow-2xl space-y-6 max-h-[95vh] overflow-y-auto my-4">
            <div className="flex justify-between items-start">
              <h2 className="text-3xl font-black text-green-800 tracking-tight">Novo Orçamento</h2>
              <button 
                type="button" 
                onClick={() => {
                  closeNewBudget();
                  if (isSimpleUser && onBack) {
                    // Voltar ao dashboard ao fechar X para simples
                    setTimeout(() => onBack(), 100);
                  }
                }} 
                className="p-2 bg-slate-50 rounded-full hover:bg-slate-100 transition-colors"
              >
                <X />
              </button>
            </div>

            {/* Row 1: Nome (left) | Telefone (right) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Nome do Cliente</label>
                <input
                  type="text"
                  value={budgetForm.client}
                  onChange={(e) => setBudgetForm(prev => ({ ...prev, client: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                  placeholder="Nome do cliente..."
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Telefone</label>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-4">
                    <select
                      value={budgetCountryCode}
                      onChange={(e) => setBudgetCountryCode(e.target.value)}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black text-xs outline-none border-2 border-transparent focus:border-green-600"
                    >
                      {UNIQUE_COUNTRIES.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.flag} +{country.code}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-8">
                    <input
                      type="text"
                      value={budgetForm.phone}
                      onChange={(e) => setBudgetForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                      placeholder={budgetCountryCode === '351' ? 'Ex: 912 345 678' : 'Ex: 123456789'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Localização (left) | Data (right) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Localização</label>
                <input
                  type="text"
                  value={budgetForm.location}
                  onChange={(e) => setBudgetForm(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                  placeholder="Localização..."
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Data</label>
                <input
                  type="date"
                  value={budgetForm.date}
                  onChange={(e) => setBudgetForm(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                />
              </div>
            </div>

            {/* Details */}
            <div className="relative">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Detalhes</label>
              <textarea
                value={budgetForm.details}
                onChange={(e) => setBudgetForm(prev => ({ ...prev, details: e.target.value }))}
                rows={4}
                className="w-full p-4 bg-green-50 rounded-2xl font-bold outline-none border-2 border-transparent focus:border-green-600 resize-y pr-12"
                placeholder="Escreva os detalhes do orçamento..."
              />
              <Mic size={16} className="absolute right-4 top-10 text-green-700/40" />
            </div>

            {/* Category Selection Cards - Split in two rows */}
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-3 px-2">Adicionar Item por Categoria</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {budgetCategories.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => addBudgetItem(cat.id)}
                    className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200 hover:border-green-400 hover:shadow-lg transition-all group"
                  >
                    <div className="text-3xl mb-2">{cat.icon}</div>
                    <div className="text-xs font-black text-green-900 uppercase tracking-tight">{cat.label}</div>
                    <div className="mt-2 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Plus size={16} className="mx-auto" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Budget Items List */}
            {budgetForm.items.length > 0 && (
              <div className="space-y-4">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block px-2">Itens do Orçamento</label>
                {budgetForm.items.map((item, idx) => {
                  const category = budgetCategories.find(c => c.id === item.category);
                  return (
                    <div key={item.id} className="bg-gradient-to-r from-slate-50 to-green-50/30 rounded-2xl p-5 border-2 border-slate-200">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{category?.icon}</div>
                          <div>
                            <div className="font-black text-slate-900 text-sm">{category?.label}</div>
                            <div className="text-xs text-slate-500 font-bold">Item #{idx + 1}</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeBudgetItem(item.id)}
                          className="p-2 bg-red-100 hover:bg-red-200 rounded-xl transition-colors"
                        >
                          <X size={18} className="text-red-600" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Descrição</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateBudgetItem(item.id, 'description', e.target.value)}
                            className="w-full p-3 bg-white rounded-xl font-bold text-sm outline-none border-2 border-slate-200 focus:border-green-500"
                            placeholder="Ex: Reparação placa..."
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Classificação</label>
                          <input
                            type="text"
                            value={item.grade}
                            onChange={(e) => updateBudgetItem(item.id, 'grade', e.target.value)}
                            className="w-full p-3 bg-white rounded-xl font-bold text-sm outline-none border-2 border-slate-200 focus:border-green-500"
                            placeholder="Ex: A+, B, Premium..."
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Valor (€)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.value}
                            onChange={(e) => updateBudgetItem(item.id, 'value', e.target.value)}
                            className="w-full p-3 bg-white rounded-xl font-black text-sm outline-none border-2 border-slate-200 focus:border-green-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Total */}
                <div className="bg-green-100 rounded-2xl p-5 border-2 border-green-300">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-black text-green-900">Total do Orçamento:</span>
                    <span className="text-3xl font-black text-green-700">{calculateBudgetTotal().toFixed(2)}€</span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4 pt-2">
              <button 
                type="button" 
                onClick={() => {
                  closeNewBudget();
                  if (isSimpleUser && onBack) {
                    // Voltar ao dashboard após cancelar para simples
                    setTimeout(() => onBack(), 100);
                  }
                }} 
                className="flex-1 py-4 rounded-2xl font-black bg-slate-100 text-slate-400 hover:bg-slate-200 transition-colors"
              >
                {isSimpleUser ? 'Voltar' : 'Cancelar'}
              </button>
              <button 
                type="submit" 
                disabled={budgetForm.items.length === 0}
                className="flex-[2] py-4 rounded-2xl font-black bg-green-800 text-white hover:bg-green-900 transition-colors shadow-xl disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                Criar Orçamento
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal: Detalhes da Assistência */}
      {selectedAssist && (
        <ModalDetalhesAssistencia
          assistencia={selectedAssist}
          onClose={() => setSelectedAssist(null)}
          onUpdate={(updated) => {
            if (onUpdate) {
              onUpdate(updated);
            }
            setSelectedAssist(updated); // Update local state
          }}
          onDelete={(id) => {
            if (onDelete) {
              onDelete(id);
            }
            setSelectedAssist(null);
          }}
          setTasks={setTasks}
          company={company}
          currentUser={currentUser}
          tasks={tasks}
          team={team}
        />
      )}

      {/* Modal: Detalhes do Orçamento */}
      {selectedBudget && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[140] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl my-4 overflow-hidden">
            {/* Header Verde Escuro */}
            <div className="bg-gradient-to-r from-[#1b5e20] to-[#2e7d32] text-white p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1 pr-4">
                  <h1 className="text-2xl font-black mb-3">{selectedBudget.client || selectedBudget.location || '—'}</h1>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    {selectedBudget.location && (
                      <div className="bg-white/10 px-3 py-1.5 rounded-lg">
                        <span className="font-bold">{selectedBudget.location}</span>
                      </div>
                    )}
                    {selectedBudget.phone && (
                      <div className="bg-white/10 px-3 py-1.5 rounded-lg">
                        <span className="font-bold">{selectedBudget.phone}</span>
                      </div>
                    )}
                    <div className="bg-white/10 px-3 py-1.5 rounded-lg">
                      <span className="font-bold">Data: {selectedBudget.date || '—'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditBudget(selectedBudget)}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors flex items-center gap-2"
                    title="Editar Orçamento"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => setSelectedBudget(null)}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
                    title="Fechar"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            </div>

            {/* Status Selection */}
            <div className="p-4 bg-gradient-to-r from-slate-50 to-green-50/30 flex gap-3 border-b border-slate-200">
              <button
                onClick={() => {
                  const updated = { ...selectedBudget, status: 'PENDENTE' };
                  setSelectedBudget(updated);
                  if (onUpdateBudget) onUpdateBudget(updated);
                }}
                className={`px-4 py-2.5 rounded-xl font-bold text-xs transition-all shadow-sm ${
                  (selectedBudget.status || 'PENDENTE') === 'PENDENTE'
                    ? 'bg-amber-500 text-white shadow-md scale-105'
                    : 'bg-white border-2 border-amber-200 text-amber-600 hover:bg-amber-50 hover:border-amber-300'
                }`}
              >
                Pendente
              </button>
              <button
                onClick={() => {
                  const updated = { ...selectedBudget, status: 'APROVADO' };
                  setSelectedBudget(updated);
                  if (onUpdateBudget) onUpdateBudget(updated);
                }}
                className={`px-4 py-2.5 rounded-xl font-bold text-xs transition-all shadow-sm ${
                  selectedBudget.status === 'APROVADO'
                    ? 'bg-green-600 text-white shadow-md scale-105'
                    : 'bg-white border-2 border-green-200 text-green-600 hover:bg-green-50 hover:border-green-300'
                }`}
              >
                Aprovado
              </button>
              <button
                onClick={() => {
                  const updated = { ...selectedBudget, status: 'REJEITADO' };
                  setSelectedBudget(updated);
                  if (onUpdateBudget) onUpdateBudget(updated);
                }}
                className={`px-4 py-2.5 rounded-xl font-bold text-xs transition-all shadow-sm ${
                  selectedBudget.status === 'REJEITADO'
                    ? 'bg-red-500 text-white shadow-md scale-105'
                    : 'bg-white border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300'
                }`}
              >
                Rejeitado
              </button>
            </div>

            {/* Content */}
            <div className="p-6 bg-slate-50 max-h-[70vh] overflow-y-auto">
              <div className="space-y-4">
                {/* Details */}
                {selectedBudget.details && (
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
                      Detalhes
                    </h3>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-600 rounded-xl p-4">
                      <p className="text-sm font-semibold text-slate-800 whitespace-pre-wrap leading-relaxed">
                        {selectedBudget.details}
                      </p>
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-black text-slate-900">Total do Orçamento</h3>
                    <span className="text-3xl font-black text-green-600">
                      {formatMoney(selectedBudget.total)}€
                    </span>
                  </div>
                </div>

                {/* Items */}
                {selectedBudget.items && selectedBudget.items.length > 0 ? (
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <div className="w-1 h-4 bg-cyan-600 rounded-full"></div>
                      Itens do Orçamento ({selectedBudget.items.length})
                    </h3>
                    <div className="space-y-3">
                      {selectedBudget.items.map((item, idx) => {
                        const category = budgetCategories.find(c => c.id === item.category);
                        return (
                          <div key={idx} className="p-4 bg-gradient-to-r from-slate-50 to-green-50/30 rounded-xl border-2 border-slate-200">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="text-2xl">{category?.icon}</div>
                                <div>
                                  <div className="font-black text-slate-900">{category?.label || item.category}</div>
                                  {item.description && (
                                    <div className="text-sm text-slate-600 font-bold mt-1">{item.description}</div>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-black text-green-600">{formatMoney(item.value)}€</div>
                              </div>
                            </div>
                            {item.grade && (
                              <div className="mt-2 pt-2 border-t border-slate-200">
                                <span className="text-xs text-slate-500 font-bold">Classificação: </span>
                                <span className="text-xs font-black text-slate-700">{item.grade}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center bg-slate-50">
                      <p className="text-slate-500 font-semibold text-sm">
                        Nenhum item adicionado
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Editar Orçamento */}
      {editingBudget && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[150] flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={submitEditBudget} className="bg-white rounded-[2.5rem] p-10 w-full max-w-4xl shadow-2xl space-y-6 max-h-[95vh] overflow-y-auto my-4">
            <div className="flex justify-between items-start">
              <h2 className="text-3xl font-black text-green-800 tracking-tight">Editar Orçamento</h2>
              <div className="flex items-center gap-2">
                <button 
                  type="button" 
                  onClick={() => {
                    if (window.confirm('Tem a certeza que deseja eliminar este orçamento? Esta ação é definitiva.')) {
                      if (onDeleteBudget && editingBudget?.id) {
                        onDeleteBudget(editingBudget.id);
                        closeEditBudget();
                      }
                    }
                  }} 
                  className="p-2 bg-red-50 rounded-xl hover:bg-red-100 transition-colors border-2 border-red-200"
                  title="Eliminar Orçamento"
                >
                  <Trash2 size={20} className="text-red-600" />
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    closeEditBudget();
                  }} 
                  className="p-2 bg-slate-50 rounded-full hover:bg-slate-100 transition-colors"
                >
                  <X />
                </button>
              </div>
            </div>

            {/* Row 1: Nome (left) | Telefone (right) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Nome do Cliente</label>
                <input
                  type="text"
                  value={budgetForm.client}
                  onChange={(e) => setBudgetForm(prev => ({ ...prev, client: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                  placeholder="Nome do cliente..."
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Telefone</label>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-4">
                    <select
                      value={budgetCountryCode}
                      onChange={(e) => setBudgetCountryCode(e.target.value)}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black text-xs outline-none border-2 border-transparent focus:border-green-600"
                    >
                      {UNIQUE_COUNTRIES.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.flag} +{country.code}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-8">
                    <input
                      type="text"
                      value={budgetForm.phone}
                      onChange={(e) => setBudgetForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                      placeholder={budgetCountryCode === '351' ? 'Ex: 912 345 678' : 'Ex: 123456789'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Localização (left) | Data (right) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Localização</label>
                <input
                  type="text"
                  value={budgetForm.location}
                  onChange={(e) => setBudgetForm(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                  placeholder="Localização..."
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Data</label>
                <input
                  type="date"
                  value={budgetForm.date}
                  onChange={(e) => setBudgetForm(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full p-4 bg-green-50 rounded-2xl font-black outline-none border-2 border-transparent focus:border-green-600"
                />
              </div>
            </div>

            {/* Details */}
            <div className="relative">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">Detalhes</label>
              <textarea
                value={budgetForm.details}
                onChange={(e) => setBudgetForm(prev => ({ ...prev, details: e.target.value }))}
                rows={4}
                className="w-full p-4 bg-green-50 rounded-2xl font-bold outline-none border-2 border-transparent focus:border-green-600 resize-y pr-12"
                placeholder="Escreva os detalhes do orçamento..."
              />
              <Mic size={16} className="absolute right-4 top-10 text-green-700/40" />
            </div>

            {/* Category Selection Cards - Split in two rows */}
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-3 px-2">Adicionar Item por Categoria</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {budgetCategories.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => addBudgetItem(cat.id)}
                    className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200 hover:border-green-400 hover:shadow-lg transition-all group"
                  >
                    <div className="text-3xl mb-2">{cat.icon}</div>
                    <div className="text-xs font-black text-green-900 uppercase tracking-tight">{cat.label}</div>
                    <div className="mt-2 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Plus size={16} className="mx-auto" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Budget Items List */}
            {budgetForm.items.length > 0 && (
              <div className="space-y-4">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block px-2">Itens do Orçamento</label>
                {budgetForm.items.map((item, idx) => {
                  const category = budgetCategories.find(c => c.id === item.category);
                  return (
                    <div key={item.id} className="bg-gradient-to-r from-slate-50 to-green-50/30 rounded-2xl p-5 border-2 border-slate-200">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{category?.icon}</div>
                          <div>
                            <div className="font-black text-slate-900 text-sm">{category?.label}</div>
                            <div className="text-xs text-slate-500 font-bold">Item #{idx + 1}</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeBudgetItem(item.id)}
                          className="p-2 bg-red-100 hover:bg-red-200 rounded-xl transition-colors"
                        >
                          <X size={18} className="text-red-600" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Descrição</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateBudgetItem(item.id, 'description', e.target.value)}
                            className="w-full p-3 bg-white rounded-xl font-bold text-sm outline-none border-2 border-slate-200 focus:border-green-500"
                            placeholder="Ex: Reparação placa..."
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Classificação</label>
                          <input
                            type="text"
                            value={item.grade}
                            onChange={(e) => updateBudgetItem(item.id, 'grade', e.target.value)}
                            className="w-full p-3 bg-white rounded-xl font-bold text-sm outline-none border-2 border-slate-200 focus:border-green-500"
                            placeholder="Ex: A+, B, Premium..."
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Valor (€)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.value}
                            onChange={(e) => updateBudgetItem(item.id, 'value', e.target.value)}
                            className="w-full p-3 bg-white rounded-xl font-black text-sm outline-none border-2 border-slate-200 focus:border-green-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Total */}
                <div className="bg-green-100 rounded-2xl p-5 border-2 border-green-300">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-black text-green-900">Total do Orçamento:</span>
                    <span className="text-3xl font-black text-green-700">{calculateBudgetTotal().toFixed(2)}€</span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4 pt-2">
              <button 
                type="button" 
                onClick={() => {
                  closeEditBudget();
                }} 
                className="flex-1 py-4 rounded-2xl font-black bg-slate-100 text-slate-400 hover:bg-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                disabled={budgetForm.items.length === 0}
                className="flex-[2] py-4 rounded-2xl font-black bg-green-800 text-white hover:bg-green-900 transition-colors shadow-xl disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                Guardar Alterações
              </button>
            </div>
          </form>
        </div>
      )}

    </div>
  );
}
